import { useGetPurchaseDetail } from 'api-hooks/order/common';
import { PurchaseDetailModel } from 'api-hooks/order/model';
import {
  RefundSalePurchaseLiteModel,
  useGetRefundPurchase,
} from 'api-hooks/refund/refund';
import { Option } from 'baseui/select';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: RefundSalePurchaseLiteModel | PurchaseDetailModel,
): Option {
  return {
    id: item.id,
    label:
      typeof item.agent === 'object'
        ? transformLabel([item.number, item.code, item.agent.name])
        : transformLabel([item.number, item.code, item.agent]),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: any) => void;
  onAfterDetailChange?: (value: PurchaseDetailModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
  id: string;
}

export default function RefundOrderSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:order_number'),
    placeholder = t('common:order_number'),
    id = '',
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetRefundPurchase,
    useDetailLazyQueryHook: useGetPurchaseDetail,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        id,
        params: {
          q,
          page,
          limit: -1,
        },
      }),
      [id],
    ),
    renderCreate: false,
    onClickCreate: React.useCallback(() => {
      props.onClose?.();
    }, [props]),
    getMemoizedDetailVariables: React.useCallback((value: any) => {
      return {
        id: value?.id ?? value,
      };
    }, []),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, PurchaseDetailModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
    listBaseOptions: { enabled: !!id },
    staleTime: undefined,
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled || !id}
      {...selectProps}
    />
  );
}
